import { useState, useMemo } from "react";

export const VALID_RATING_SCORES = new Set([-1, 0, 1, 2, 3, 4, 5]);

// ─── Constants ────────────────────────────────────────────────────────────────
export const PURPLE = "#6C00E6";
export const EMOJI_FONT = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
export const CARD_SHADOW = "0 2px 10px rgba(0,0,0,0.08)";

export const createEmptyTeamRatingBlock = () => ({
  clientId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  teamMemberId: "",
  score: null,
  comment: "",
  mediaFiles: [],
  mediaUrls: [],
  videoDuration: null,
});

export const createDefaultRatingValue = () => ({
  teamMemberRatings: [createEmptyTeamRatingBlock()],
  place: { score: null, comment: "", mediaFiles: [], mediaUrls: [], videoDuration: null },
});

export const toDialogValue = (apiRating) => {
  const teamMemberRatings = (apiRating?.teamMemberRatings || []).map((entry) => ({
    clientId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    teamMemberId: String(entry?.teamMemberId?._id || entry?.teamMemberId || entry?.employeeId?._id || entry?.employeeId || ""),
    score: typeof entry?.rating === "number" ? entry.rating : null,
    comment: entry?.comment || "",
    mediaFiles: [],
    mediaUrls: Array.isArray(entry?.mediaUrls) ? entry.mediaUrls : [],
    videoDuration: typeof entry?.videoDuration === "number" ? entry.videoDuration : null,
  }));

  const place = {
    score:
      typeof apiRating?.placeRating?.rating === "number"
        ? apiRating.placeRating.rating
        : null,
    comment: apiRating?.placeRating?.comment || "",
    mediaFiles: [],
    mediaUrls: Array.isArray(apiRating?.placeRating?.mediaUrls)
      ? apiRating.placeRating.mediaUrls
      : [],
    videoDuration: typeof apiRating?.placeRating?.videoDuration === "number" ? apiRating.placeRating.videoDuration : null,
  };

  return {
    teamMemberRatings: teamMemberRatings.length
      ? teamMemberRatings
      : [createEmptyTeamRatingBlock()],
    place,
  };
};

export const validateDialogValue = (dialogValue) => {
  const teamBlocks = Array.isArray(dialogValue?.teamMemberRatings)
    ? dialogValue.teamMemberRatings
    : [];
  const hasPlaceScore = typeof dialogValue?.place?.score === "number";
  const hasTeamScore = teamBlocks.some((block) => typeof block?.score === "number");

  if (!hasPlaceScore && !hasTeamScore) {
    return "please add at least one team member or place rating";
  }

  for (const block of teamBlocks) {
    if (typeof block?.score !== "number") continue;
    if (!VALID_RATING_SCORES.has(block.score)) {
      return "invalid team member rating score";
    }
    if (!block?.teamMemberId) {
      return "please select a team member for each rated row";
    }
    if ((block?.comment || "").length > 500) {
      return "team member comment cannot exceed 500 characters";
    }
  }

  if (hasPlaceScore && !VALID_RATING_SCORES.has(dialogValue?.place?.score)) {
    return "invalid place rating score";
  }
  if ((dialogValue?.place?.comment || "").length > 500) {
    return "place comment cannot exceed 500 characters";
  }

  return "";
};

export const buildSubmitPayload = async ({
  dialogValue,
  conversationId,
  uploadRatingMediaFiles,
}) => {
  const payload = { conversationId };
  const teamBlocks = Array.isArray(dialogValue?.teamMemberRatings)
    ? dialogValue.teamMemberRatings
    : [];

  const teamMemberRatings = [];
  for (const block of teamBlocks) {
    if (typeof block?.score !== "number" || !block?.teamMemberId) continue;
    // Keep deterministic upload sequencing by block.
    // eslint-disable-next-line no-await-in-loop
    const uploadedUrls = await uploadRatingMediaFiles(block?.mediaFiles || []);
    const entry = {
      teamMemberId: String(block.teamMemberId),
      rating: block.score,
      comment: (block?.comment || "").trim(),
      mediaUrls: [...(block?.mediaUrls || []), ...uploadedUrls],
    };
    if (typeof block?.videoDuration === "number") entry.videoDuration = block.videoDuration;
    teamMemberRatings.push(entry);
  }
  if (teamMemberRatings.length) payload.teamMemberRatings = teamMemberRatings;

  if (typeof dialogValue?.place?.score === "number") {
    const uploadedUrls = await uploadRatingMediaFiles(dialogValue?.place?.mediaFiles || []);
    const placeEntry = {
      rating: dialogValue.place.score,
      comment: (dialogValue?.place?.comment || "").trim(),
      mediaUrls: [...(dialogValue?.place?.mediaUrls || []), ...uploadedUrls],
    };
    if (typeof dialogValue?.place?.videoDuration === "number") placeEntry.videoDuration = dialogValue.place.videoDuration;
    payload.placeRating = placeEntry;
  }

  return payload;
};

// ─── Dashboard Helpers ────────────────────────────────────────────────────────
export const formatCompact = (n) => {
  if (n == null) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

export const formatScoreDisplay = (v) => {
  if (v == null || Number.isNaN(v)) return "—";
  const x = Math.round(v * 10) / 10;
  return Math.round(x) === x ? String(x) : x.toFixed(1);
};

export const emojiForAvg = (avg) => {
  if (avg == null || Number.isNaN(avg)) return "😐";
  const map = { "-1": "🤬", 0: "😡", 1: "😐", 2: "🙂", 3: "😀", 4: "😁", 5: "🤩" };
  return map[Math.max(-1, Math.min(5, Math.round(avg)))] || "🙂";
};

/**
 * Shared Chart.js emoji plugin logic.
 * Components can register this or use the RatingBarChart component.
 */
export const barTopEmojiPlugin = {
  id: "barTopEmoji",
  afterDatasetsDraw(chart) {
    const cfg = chart.options.plugins?.barTopEmoji;
    if (!cfg?.emojis?.length) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;
    const { ctx } = chart;
    const fontSize = cfg.fontSize ?? 20;
    const pad = cfg.pad ?? 8;
    meta.data.forEach((el, i) => {
      if (!cfg.emojis[i]) return;
      const { x, y } = el.getProps(["x", "y"], true);
      ctx.save();
      ctx.font = `${fontSize}px ${EMOJI_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText(cfg.emojis[i], x, y - pad);
      ctx.restore();
    });
  },
};

/**
 * Custom hook to manage rating selection filter logic.
 * 
 * @param {Array} ratingFilters - The static rating filters definition (scores and percents).
 * @param {number} totalRatingsCount - Total number of ratings for the business/team/place.
 */
export const useRatingFilter = (ratingFilters, totalRatingsCount) => {
  const [filterChecked, setFilterChecked] = useState(() =>
    ratingFilters.reduce((acc, { score }) => ({ ...acc, [score]: true }), {})
  );

  const handleFilterChange = (score, checked) =>
    setFilterChecked((prev) => {
      const next = { ...prev, [score]: checked };
      // Ensure at least one filter remains checked
      return ratingFilters.some((r) => next[r.score]) ? next : prev;
    });

  const filterDerived = useMemo(() => {
    let shareChecked = 0;
    let weightedSum = 0;

    ratingFilters.forEach(({ score, percent }) => {
      if (filterChecked[score]) {
        shareChecked += percent;
        weightedSum += score * percent;
      }
    });

    return {
      avg: shareChecked > 0 ? weightedSum / shareChecked : null,
      totalFiltered: shareChecked > 0 ? Math.round(totalRatingsCount * (shareChecked / 100)) : 0,
      displayRows: ratingFilters.map((row) => ({
        ...row,
        included: !!filterChecked[row.score],
        displayPercent: filterChecked[row.score] && shareChecked > 0
          ? Math.round((row.percent / shareChecked) * 1000) / 10
          : 0,
      })),
    };
  }, [filterChecked, ratingFilters, totalRatingsCount]);

  return {
    filterChecked,
    filterDerived,
    handleFilterChange,
  };
};
